import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, formatMinor } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = {
  districts: [],
  selected: 0,
  loaded: false,
  loading: false,
  error: null,
  notice: null,
  ticketError: null,
};

let loadPromise = null;

export function _resetTicketsState(overrides = {}) {
  Object.assign(
    state,
    {
      districts: [],
      selected: 0,
      loaded: false,
      loading: false,
      error: null,
      notice: null,
      ticketError: null,
    },
    overrides,
  );
  loadPromise = null;
}

export function mapDistricts(content) {
  return (content?.districts || [])
    .filter((district) => district.status === 'open')
    .map((district) => ({
      id: district.districtId,
      label: String(district.name || district.districtId).toUpperCase(),
      riskTier: String(district.riskTier || '--').toUpperCase(),
    }));
}

export function ticketErrorMessage(error) {
  if (!error) return 'TICKET LINE DOWN';
  if (error.code === 'CORE-409-004') return 'TICKET ALREADY ISSUED TODAY';
  if (error.code === 'CORE-400-024') return 'NO TICKETS ARE SOLD ON PURGE DAY';
  if (error.code === 'CORE-400-003') return 'INSUFFICIENT CASH FOR THE TARIFF';
  if (error.code === 'CORE-400-005') return 'ALREADY IN THAT DISTRICT';
  if (error.code === 'CORE-404-012') return 'TICKET LINE CLOSED // V2 OFFLINE';
  return coreErrorMessage(error);
}

function defaultSelection(districts, currentDistrictId) {
  const index = districts.findIndex((district) => district.id !== currentDistrictId);
  return index >= 0 ? index : 0;
}

export async function loadTicketsData() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    state.loading = true;
    try {
      const session = useCoreSession.getState();
      const content = await session.loadContent();
      const districts = mapDistricts(content);
      state.districts = districts;
      state.selected = defaultSelection(districts, useCoreSession.getState().state?.districtId);
      state.loaded = true;
    } catch (error) {
      state.error = coreErrorMessage(error);
      state.loaded = true;
    }

    try {
      await useCoreSession.getState().loadTicket();
      state.ticketError = null;
    } catch (error) {
      state.ticketError = ticketErrorMessage(error);
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

function selectedDistrict() {
  return state.districts[state.selected] || state.districts[0] || null;
}

function districtById(id) {
  return state.districts.find((district) => district.id === id) || null;
}

function ticketDestination(ticket) {
  const district = districtById(ticket?.toDistrictId);
  return {
    label: district?.label || String(ticket?.toDistrictId || '--').toUpperCase(),
    riskTier: district?.riskTier || '--',
  };
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

async function buySelected() {
  const district = selectedDistrict();
  if (!district || state.loading) return;
  state.loading = true;
  state.error = null;
  state.notice = null;
  try {
    await useCoreSession.getState().buyTicket(district.id);
    state.notice = `TICKET ISSUED // ${district.label}`;
    state.ticketError = null;
  } catch (error) {
    state.error = ticketErrorMessage(error);
    const issued = error?.payload?.details?.ticket;
    if (issued) useCoreSession.setState({ ticket: issued });
  } finally {
    state.loading = false;
  }
}

function drawTop(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // TICKETS', 8, 4, TERMINAL.TEXT);
  if (!mobile) text(ctx, 'NEXT WORLD DAY TICKET', 216, 4, TERMINAL.TEXT_DIM);
  text(ctx, online ? 'TICKET LINE' : 'OFFLINE', safe.width - 88, 4, online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawDistricts(ctx, x, y, w, h, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'DESTINATIONS // RISK TIER');
  text(ctx, mobile ? '#  DISTRICT' : '#  DISTRICT            RISK', x + 10, y + 24, TERMINAL.TEXT_DIM);
  rule(ctx, x + 8, y + 40, w - 16);

  const rowH = mobile ? 44 : 32;
  const top = y + 50;
  state.districts.forEach((district, index) => {
    const rowY = top + index * rowH;
    const selected = index === state.selected;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, rowH - 4);
    }
    text(ctx, selected ? '>' : ' ', x + 10, rowY, TERMINAL.TEXT_BRIGHT);
    if (mobile) {
      text(ctx, `${index + 1} ${district.label.slice(0, 24)}`, x + 22, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
      text(ctx, `RISK ${district.riskTier}`, x + 22, rowY + 16, TERMINAL.TEXT_DIM);
    } else {
      const line = `${index + 1}  ${district.label.padEnd(18).slice(0, 18)} ${district.riskTier}`;
      text(ctx, line, x + 22, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    }
    interactiveRegistry.register({
      id: `tickets-district-${district.id}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: rowH,
      onClick: () => {
        state.selected = index;
        state.error = null;
        state.notice = null;
      },
    });
  });

  if (!state.districts.length) {
    text(ctx, state.loaded ? 'NO OPEN DISTRICTS' : 'READING THE LINE...', x + 10, top, TERMINAL.TEXT_DIM);
  }
}

function drawTicket(ctx, x, y, w, h, ticket, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, ticket ? 'TICKET ON FILE' : 'NO TICKET ON FILE');
  const line = mobile ? 18 : 22;
  let row = y + 30;

  if (ticket) {
    const destination = ticketDestination(ticket);
    text(ctx, 'DEST', x + 10, row, TERMINAL.TEXT_DIM);
    text(ctx, destination.label.slice(0, mobile ? 20 : 18), x + 62, row, TERMINAL.TEXT_BRIGHT);
    row += line;
    text(ctx, 'TARIFF', x + 10, row, TERMINAL.TEXT_DIM);
    text(ctx, formatMinor(ticket.tariffPaid), x + 62, row, TERMINAL.TEXT);
    row += line;
    text(ctx, 'RISK', x + 10, row, TERMINAL.TEXT_DIM);
    text(ctx, destination.riskTier, x + 62, row, TERMINAL.TEXT);
    row += line;
    text(ctx, 'ISSUED', x + 10, row, TERMINAL.TEXT_DIM);
    text(ctx, `DAY ${ticket.dayIndex}`, x + 62, row, TERMINAL.TEXT);
    row += line;
    text(ctx, 'VALID', x + 10, row, TERMINAL.TEXT_DIM);
    text(ctx, `DAY ${ticket.dayIndex + 1}`, x + 62, row, TERMINAL.TEXT);
    row += line;
    text(ctx, 'SEASON', x + 10, row, TERMINAL.TEXT_DIM);
    text(ctx, String(ticket.seasonId || '--').slice(-8).toUpperCase(), x + 62, row, TERMINAL.TEXT_DIM);
  } else {
    text(ctx, 'NO TICKET = LAY LOW.', x + 10, row, TERMINAL.TEXT);
    row += line;
    text(ctx, 'STAY PUT; POSITIONS PERSIST.', x + 10, row, TERMINAL.TEXT_DIM);
    row += line;
    text(ctx, 'TARIFF FIXED. NO HAGGLING.', x + 10, row, TERMINAL.TEXT_DIM);
    row += line;
    text(ctx, 'WHEN THE TICKET IS ISSUED.', x + 10, row, TERMINAL.TEXT_DIM);
    row += line;
    text(ctx, 'ONE TICKET PER PLAYER PER DAY.', x + 10, row, TERMINAL.TEXT_DIM);
  }

  const note = state.notice || state.error || state.ticketError;
  if (note) {
    const color = state.error || state.ticketError ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN;
    text(ctx, `// ${note}`.slice(0, mobile ? 40 : 30), x + 10, y + h - 18, color);
  }
}

function drawFooter(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  const canBuy = online && Boolean(selectedDistrict()) && !state.loading;
  const label = state.loading ? 'ISSUING...' : 'BUY TICKET';

  if (mobile) {
    const buyY = 530;
    const backY = 584;
    DopeUI.Button(ctx, 8, buyY, safe.width - 16, 44, label, canBuy ? 'hover' : 'disabled');
    if (canBuy) {
      interactiveRegistry.register({
        id: 'tickets-buy',
        x: 8,
        y: buyY,
        w: safe.width - 16,
        h: 44,
        onClick: buySelected,
      });
    }
    DopeUI.Button(ctx, 8, backY, safe.width - 16, 44, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'tickets-back',
      x: 8,
      y: backY,
      w: safe.width - 16,
      h: 44,
      onClick: () => navigateTo('main_menu'),
    });
    return;
  }

  rule(ctx, 0, 320, safe.width);
  text(ctx, 'ONE TICKET. ONE DAY.', 8, 330, TERMINAL.TEXT_DIM);
  DopeUI.Button(ctx, 8, 344, 304, 28, label, canBuy ? 'hover' : 'disabled');
  if (canBuy) {
    interactiveRegistry.register({
      id: 'tickets-buy',
      x: 8,
      y: 344,
      w: 304,
      h: 28,
      onClick: buySelected,
    });
  }
  DopeUI.Button(ctx, 320, 344, safe.width - 328, 28, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'tickets-back',
    x: 320,
    y: 344,
    w: safe.width - 328,
    h: 28,
    onClick: () => navigateTo('main_menu'),
  });
  text(ctx, 'DOPE CHAIN WARS // TICKETS', safe.width - 216, 384, TERMINAL.GREEN_DARK);
}

registerView('tickets', function renderTickets(ctx, time, width, height) {
  interactiveRegistry.clear();
  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !loadPromise) {
    loadTicketsData();
  }

  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);

  drawTop(ctx, safe, session);
  const mobile = safe.width < 500;
  const ticket = session.ticket ?? null;

  if (mobile) {
    drawDistricts(ctx, 8, 32, safe.width - 16, 230, true);
    drawTicket(ctx, 8, 270, safe.width - 16, 196, ticket, true);
  } else {
    drawDistricts(ctx, 8, 40, 360, 264);
    drawTicket(ctx, 376, 40, 256, 264, ticket);
  }
  drawFooter(ctx, safe, session);
});

export default 'tickets';
